import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useLongPress } from "@sukha/hooks";

const buttonVariants = cva(
  "inline-flex justify-center items-center gap-2 aria-invalid:border-destructive disabled:opacity-50 focus-visible:border-ring rounded-md aria-invalid:ring-destructive/20 focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:aria-invalid:ring-destructive/40 font-medium text-sm whitespace-nowrap transition-[color,box-shadow] cursor-pointer disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-hidden",
  {
    variants: {
      variant: {
        default:
          "bg-secondary-300/70 text-primary-foreground shadow-xs enabled:hover:bg-secondary-300/80 disabled:bg-primary-700",
        /** Teal brand primary — use for the main action when it should read as “primary” vs golden `default`. */
        primary:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 focus-visible:ring-ring/50 disabled:opacity-50",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 disabled:bg-destructive-700",
        /*
         * A TRUE outline: the surface shows straight through, and the edge is
         * the component.
         *
         * It was `border-input bg-background` — `oklch(0.205 0 0 / 0.7)` ringed
         * in `oklch(0.922 0 0)`, a near-black block with a bright ring, on top
         * of the translucent panel it was meant to belong to. 65 buttons.
         *
         * WCAG 1.4.11 asks 3:1 for a component boundary against EVERY adjacent
         * colour — the surface outside and the button's own fill inside. That
         * second half is why a translucent white fill had to go: `bg-white/5`
         * brightens alongside the border, so the inner edge never separates.
         * Measured across the three surfaces these sit on (glass panel, dialog,
         * page ground), worst of both adjacencies:
         *
         *   white/20 + bg-white/5     1.57   shipped first, failed 1.4.11
         *   white/50 + transparent    3.17   passes, no margin
         *   white/55 + transparent    3.46   PASSES, and is nothing like the
         *                                    8.57 of the ring it replaced
         *
         * Hover raises the edge with the fill so the state stays compliant:
         * worst boundary 3.38, worst label 4.97. Label contrast at rest is 7.33.
         */
        outline:
          "border border-white/55 bg-transparent text-primary-foreground shadow-xs hover:bg-secondary-300/20 hover:text-primary-foreground hover:border-white/70",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 disabled:bg-secondary-700",
        ghost:
          "hover:bg-accent hover:text-accent-foreground hover:bg-primary/90",
        link: "text-primary underline-offset-4 hover:underline",
        circle:
          "bg-white/10 hover:bg-white/20 text-primary-foreground/60 hover:text-primary-foreground rounded-full",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        circle: "size-8 p-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends Omit<React.ComponentProps<"button">, "onClick">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onLongClick?: (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => void;
  longPressDelay?: number;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  onLongClick,
  longPressDelay = 500,
  onClick,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  // Always call the hook, but only use its result when needed
  // This ensures the hook is called consistently on every render
  const longPressCallback = React.useCallback(
    (
      e:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ) => {
      if (onLongClick) onLongClick(e);
    },
    [onLongClick]
  );

  const clickCallback = React.useCallback(
    (
      e:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ) => {
      if (onClick) {
        // Handle the type casting in a more type-safe way
        if ("touches" in e) {
          // For touch events, we don't call onClick as it expects a mouse event
          // This is consistent with how browsers handle touch vs click events
          // onClick will be triggered by the browser's click event after touchend
          return;
        } else {
          // It's a mouse event, which is what onClick expects
          onClick(e);
        }
      }
    },
    [onClick]
  );

  // Always call the hook, but with no-op functions if handlers aren't provided
  // Use the generic type parameter to specify HTMLButtonElement
  const { handlerProps } = useLongPress<HTMLButtonElement>(
    longPressCallback,
    clickCallback,
    { delay: longPressDelay }
  );

  // Only apply the handler props if onLongClick is provided
  const eventHandlers = onLongClick ? handlerProps : onClick ? { onClick } : {};

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...eventHandlers}
      {...props}
    />
  );
}

export { Button, buttonVariants };
